import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Loader2, Search, ExternalLink, FileDown, Upload } from "lucide-react";
import Configurator from "../components/franchise/Configurator";
import EnquiryDetail, { adminApi } from "../components/franchise/AdminEnquiryDetail";
import CatalogueEditor from "../components/franchise/AdminCatalogueEditor";
import { API, PUB, EMPTY_CONFIG, inr } from "../components/franchise/fq";
import "../components/franchise/franchise.css";

function EnquiryList({ token, isSA }) {
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(null);
  const load = useCallback(() => adminApi(token, `/enquiries?${new URLSearchParams({ ...(q ? { q } : {}), ...(status ? { status } : {}) })}`).then(setD).catch((e) => toast.error(e.message)), [token, q, status]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Loader2 className="w-5 h-5 animate-spin" />;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <button className={`px-3 py-1 rounded-full text-xs border ${!status ? "bg-primary text-white" : ""}`} onClick={() => setStatus("")} data-testid="enq-filter-all">All</button>
        {d.statuses.map((s) => <button key={s} className={`px-3 py-1 rounded-full text-xs border ${status === s ? "bg-primary text-white" : ""}`} onClick={() => setStatus(s)} data-testid={`enq-filter-${s.replace(/\s/g, "-").toLowerCase()}`}>{s} ({d.counts[s] || 0})</button>)}
      </div>
      <div className="relative max-w-sm"><Search className="w-4 h-4 absolute left-2 top-2.5 text-muted-foreground" /><Input className="pl-8" placeholder="Search name, phone, city, ref" value={q} onChange={(e) => setQ(e.target.value)} data-testid="enq-search" /></div>
      <div className="overflow-x-auto border rounded-md">
        <table className="w-full text-sm">
          <thead className="bg-muted text-xs uppercase"><tr>{["Ref", "Applicant", "City", "Source", "Format / Model", "Quote", "Status", "Assigned", "Follow-up", "Created"].map((h) => <th key={h} className="text-left p-2">{h}</th>)}</tr></thead>
          <tbody>
            {d.enquiries.map((e) => (
              <tr key={e.enquiry_id} className="border-t hover:bg-muted/40 cursor-pointer" onClick={() => setOpen(e.enquiry_id)} data-testid={`enq-row-${e.enquiry_id}`}>
                <td className="p-2 font-mono text-xs">{e.enquiry_id}</td><td className="p-2">{e.contact?.name}<div className="text-xs text-muted-foreground">{e.contact?.phone}</div></td>
                <td className="p-2">{e.contact?.city}</td><td className="p-2">{e.source}</td>
                <td className="p-2">{e.latest_quote?.format} / {e.latest_quote?.model}</td>
                <td className="p-2 text-xs">{e.quote_id} R{e.latest_quote?.revision} <Badge variant="outline">{e.latest_quote?.status}</Badge></td>
                <td className="p-2"><Badge>{e.status}</Badge></td><td className="p-2">{e.assigned_to || "-"}</td><td className="p-2">{e.next_follow_up || "-"}</td><td className="p-2 text-xs">{e.created_at?.slice(0, 10)}</td>
              </tr>
            ))}
            {!d.enquiries.length && <tr><td colSpan={10} className="p-6 text-center text-muted-foreground">No enquiries yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Enquiry {open}</DialogTitle></DialogHeader>
          {open && <EnquiryDetail token={token} enquiryId={open} isSA={isSA} onChanged={load} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DirectContact({ token, config, result, onCreated }) {
  const [c, setC] = useState({ name: "", phone: "", email: "", city: config.location.city, country: "India", follow_up_method: "WhatsApp", message: "" });
  const [source, setSource] = useState("WhatsApp");
  const [perm, setPerm] = useState("");
  const [mkt, setMkt] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { const r = await adminApi(token, "/enquiries", { method: "POST", body: { source, contact: c, contact_permission: perm, inputs: config, marketing_consent: mkt } }); toast.success(`Created ${r.enquiry_id} with quote ${r.quote_id}. Nothing has been sent.`); onCreated(r.enquiry_id); }
    catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  const s = (k, v) => setC({ ...c, [k]: v });
  return (
    <div className="space-y-4" data-testid="direct-contact-form">
      <p className="text-sm">Quote: <b>{result.format.label} · {result.model.label}</b> — known one-time {inr(result.sections.A.known_subtotal)}{result.sections.A.has_pending ? " + pending" : ""}, fixed monthly {inr(result.sections.B.known_subtotal)} before taxes.</p>
      <div className="flex gap-2">{["WhatsApp", "Email", "Phone", "Website"].map((x) => <button key={x} type="button" className={`fq-choice fq-pill ${source === x ? "is-active" : ""}`} onClick={() => setSource(x)} data-testid={`direct-source-${x.toLowerCase()}`}>{x}</button>)}</div>
      <div className="grid sm:grid-cols-3 gap-3">
        {[["name", "Name *"], ["phone", "Phone"], ["email", "Email"], ["city", "City"], ["country", "Country"]].map(([k, l]) => <div key={k}><Label>{l}</Label><Input value={c[k]} onChange={(e) => s(k, e.target.value)} data-testid={`direct-${k}`} /></div>)}
      </div>
      <div><Label>How did the applicant give permission to be contacted? *</Label><Input value={perm} onChange={(e) => setPerm(e.target.value)} placeholder="e.g. Applicant messaged on WhatsApp on 12 Jun asking for a quote" data-testid="direct-permission" /></div>
      <div><Label>Notes from the applicant</Label><Textarea rows={2} value={c.message} onChange={(e) => s("message", e.target.value)} /></div>
      <label className="flex gap-2 text-sm items-center"><Checkbox checked={mkt} onCheckedChange={(v) => setMkt(!!v)} data-testid="direct-marketing" />Applicant explicitly opted in to marketing updates</label>
      <Button className="fq-cta" onClick={submit} disabled={busy || !c.name || perm.trim().length < 5} data-testid="direct-create">{busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Create enquiry & quote (no message sent)</Button>
    </div>
  );
}

function DirectQuoteLoader(props) {
  const [cat, setCat] = useState(null);
  useEffect(() => { fetch(`${PUB}/catalogue`).then((r) => r.json()).then((d) => setCat(d.catalogue)); }, []);
  if (!cat) return <Loader2 className="w-5 h-5 animate-spin" />;
  return <DirectQuoteWithCat {...props} cat={cat} />;
}

function DirectQuoteWithCat({ token, onCreated, cat }) {
  const [config, setConfig] = useState(EMPTY_CONFIG);
  const [step, setStep] = useState(1);
  return (
    <div className="fq-root rounded-lg p-4 md:p-6" data-testid="direct-quote-builder">
      <p className="text-sm mb-4">Uses the same selector and server-side calculation as the public page. The applicant does not need to complete the website.</p>
      <Configurator cat={cat} config={config} setConfig={setConfig} step={step} setStep={setStep}
        renderFinal={({ result }) => <DirectContact token={token} config={config} result={result} onCreated={(id) => { setConfig(EMPTY_CONFIG); setStep(1); onCreated(id); }} />} />
    </div>
  );
}

function Community({ token }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { adminApi(token, "/community").then((d) => setRows(d.subscribers)).catch((e) => toast.error(e.message)); }, [token]);
  if (!rows) return <Loader2 className="w-5 h-5 animate-spin" />;
  return (
    <table className="w-full text-sm border rounded" data-testid="community-table">
      <thead className="bg-muted text-xs uppercase"><tr>{["Ref", "Name", "Email", "Phone", "City", "Interest", "Consent", "Date"].map((h) => <th key={h} className="p-2 text-left">{h}</th>)}</tr></thead>
      <tbody>{rows.map((r) => <tr key={r.subscriber_id} className="border-t"><td className="p-2 font-mono text-xs">{r.subscriber_id}</td><td className="p-2">{r.name}</td><td className="p-2">{r.email || "-"}</td><td className="p-2">{r.phone || "-"}</td><td className="p-2">{r.city || "-"}</td><td className="p-2">{r.interest || "-"}</td><td className="p-2">{r.consent_at?.slice(0, 10)}</td><td className="p-2">{r.created_at?.slice(0, 10)}</td></tr>)}
        {!rows.length && <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">No subscribers yet.</td></tr>}</tbody>
    </table>
  );
}

function GuidesAndIntegration({ token, isSA }) {
  const [guides, setGuides] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => adminApi(token, "/guides").then((d) => setGuides(d.guides)).catch(() => {}), [token]);
  useEffect(() => { load(); }, [load]);
  const pageUrl = `${window.location.origin}/franchise`;
  const upload = async (file) => {
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    const r = await fetch(`${API}/api/franchise-quotes/guides/upload?token=${encodeURIComponent(token)}&label=${encodeURIComponent(file.name)}`, { method: "POST", body: fd });
    const d = await r.json().catch(() => ({}));
    r.ok ? toast.success("Uploaded — activate it to use as the general guide") : toast.error(d.detail || "Upload failed");
    setBusy(false); load();
  };
  const activate = (id, active) => adminApi(token, `/guides/${id}/active`, { method: "POST", body: { active } }).then(() => { toast.success(active ? "Guide activated" : "Using catalogue-generated guide"); load(); }).catch((e) => toast.error(e.message));
  const embed = `<iframe src="${pageUrl}?embed=1" title="Purnabramha franchise" style="width:100%;min-height:1400px;border:0" loading="lazy"></iframe>`;
  return (
    <div className="space-y-6 text-sm">
      <section className="space-y-2" data-testid="integration-info">
        <h4 className="font-semibold">Public page & website integration</h4>
        <p>Public URL: <a className="underline" href={pageUrl} target="_blank" rel="noreferrer" data-testid="integration-public-url">{pageUrl}</a> <ExternalLink className="w-3 h-3 inline" /></p>
        <p>Direct tab links: <code>{pageUrl}?tab=details</code> · <code>{pageUrl}?tab=quote&amp;step=1</code> · <code>{pageUrl}?tab=invest</code></p>
        <p>Embed (no header/footer):</p>
        <pre className="bg-muted p-2 rounded text-xs whitespace-pre-wrap" data-testid="integration-embed-code">{embed}</pre>
        <p className="text-xs text-muted-foreground">purnabramha.com/franchise is unchanged. Updating it requires access to that website's code or hosting (link the existing enquiry buttons to the URL above, or paste the embed code).</p>
      </section>
      <section className="space-y-2">
        <h4 className="font-semibold">General franchise guide</h4>
        <a className="underline inline-flex items-center gap-1" href={`${API}/api/franchise-quotes/general-guide.pdf?token=${encodeURIComponent(token)}`} target="_blank" rel="noreferrer" data-testid="guide-generated-download"><FileDown className="w-4 h-4" />Download catalogue-generated guide (current version)</a>
        {isSA && <label className="flex items-center gap-2 cursor-pointer w-fit"><Upload className="w-4 h-4" /><span className="underline">{busy ? "Uploading…" : "Upload approved guide PDF"}</span><input type="file" accept="application/pdf" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="guide-upload-input" /></label>}
        {guides.map((g) => (
          <div key={g.id} className="flex items-center gap-3 border rounded p-2" data-testid={`guide-row-${g.id}`}>
            <span className="flex-1">{g.label} · {(g.size / 1024).toFixed(0)} KB · {g.uploaded_at.slice(0, 10)} by {g.uploaded_by}</span>
            {g.active ? <Badge>Active</Badge> : null}
            {isSA && <Button size="sm" variant="outline" onClick={() => activate(g.id, !g.active)}>{g.active ? "Deactivate" : "Activate"}</Button>}
          </div>
        ))}
      </section>
    </div>
  );
}

export default function FranchiseEnquiries() {
  const { session } = useAuth();
  const token = session?.token;
  const isSA = session?.is_super_admin === true;
  const [tab, setTab] = useState("enquiries");
  const [key, setKey] = useState(0);
  if (!(session?.is_super_admin || session?.is_admin)) return <div className="p-8" data-testid="fe-no-access">Admin access required.</div>;
  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="franchise-enquiries-page">
      <div><h1 className="text-2xl font-bold">Franchise Enquiries & Quotes</h1><p className="text-sm text-muted-foreground">Website and direct enquiries, quote revisions, combined PDFs, share links and the versioned pricing catalogue.</p></div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="enquiries" data-testid="fe-tab-enquiries">Enquiries</TabsTrigger>
          <TabsTrigger value="direct" data-testid="fe-tab-direct">Create quote for direct enquiry</TabsTrigger>
          <TabsTrigger value="catalogue" data-testid="fe-tab-catalogue">Pricing catalogue</TabsTrigger>
          <TabsTrigger value="community" data-testid="fe-tab-community">Community updates</TabsTrigger>
          <TabsTrigger value="guides" data-testid="fe-tab-guides">Guide & integration</TabsTrigger>
        </TabsList>
        <TabsContent value="enquiries"><EnquiryList key={key} token={token} isSA={isSA} /></TabsContent>
        <TabsContent value="direct"><DirectQuoteLoader token={token} onCreated={() => { setKey(key + 1); setTab("enquiries"); }} /></TabsContent>
        <TabsContent value="catalogue"><CatalogueEditor token={token} isSA={isSA} /></TabsContent>
        <TabsContent value="community"><Community token={token} /></TabsContent>
        <TabsContent value="guides"><GuidesAndIntegration token={token} isSA={isSA} /></TabsContent>
      </Tabs>
    </div>
  );
}
